import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';

import type { ConversationInsight } from '../api/types';
import { useProject } from '../projects/context';
import { useProjectApi } from '../projects/useProjectApi';
import { cleanAiText } from './aiText';
import { fmtDateTime } from './format';

function Insight({ item, projectId, defaultOpenTranscript = false }: { item: ConversationInsight; projectId: string; defaultOpenTranscript?: boolean }) {
  const recUrl = `/api/v1/projects/${encodeURIComponent(projectId)}/calls/${encodeURIComponent(item.call_id)}/recording`;
  const mins = item.duration_seconds ? Math.max(1, Math.round(item.duration_seconds / 60)) : null;
  return (
    <article>
      <p className="muted">
        Gespräch vom {fmtDateTime(item.called_at)}
        {item.agent_name && ` · ${item.agent_name}`}
        {mins && ` · ca. ${mins} Min.`}
      </p>
      {item.has_recording && (
        <div style={{ margin: '8px 0 10px' }}>
          <div className="small" style={{ fontWeight: 600, marginBottom: '4px' }}>
            Gesprächsaufzeichnung anhören:
          </div>
          <audio controls preload="none" src={recUrl} style={{ width: '100%', maxWidth: '480px', height: '38px' }}>
            Ihr Browser unterstützt keine Audio-Wiedergabe.
          </audio>
        </div>
      )}
      <p className="alert alert-info">
        {item.source_mode === 'AGENT_ONLY'
          ? 'Nur Mitarbeiterstimme aufgenommen. Kundenangaben sind aus dessen Aussagen abgeleitet, nicht direkt bestätigt.'
          : item.source_mode === 'BOTH'
            ? 'KI-Auswertung einer beidseitigen Aufnahme. Gesprächsangaben sind noch keine geprüften Objektangaben.'
            : 'Aufnahmeumfang unbekannt. Diese KI-Erkenntnisse sind nicht als bestätigte Kundenangaben zu behandeln.'}
      </p>
      {item.conflict && <p className="alert alert-warn">Widerspruch zur Erfassung: {item.conflict}</p>}
      <p>{cleanAiText(item.summary)}</p>
      {item.property && <p><strong>Objektangaben aus dem Gespräch: </strong>{item.property}</p>}
      {item.next_step && <p><strong>Erkannter nächster Schritt: </strong>{item.next_step}</p>}
      {item.callback_note && <p><strong>Rückruf / Zusage laut Auswertung: </strong>{item.callback_note}</p>}
      {(item.next_step || item.callback_note) && <p className="muted">Diese Anzeige bestätigt keinen Kundentermin. Angelegte interne Aufgaben stehen unter „Rückfragen & Aufgaben“.</p>}
      {item.open_questions.length > 0 && <><h3>Noch zu klären</h3><ul>{item.open_questions.map((text, i) => <li key={i}>{text}</li>)}</ul></>}
      {item.risks.length > 0 && <><h3>Hinweise und Risiken</h3><ul>{item.risks.map((text, i) => <li key={i}>{text}</li>)}</ul></>}
      {item.statements.length > 0 && <details><summary>Erkannte Kernaussagen (KI, keine wörtlichen Belege)</summary><ul>{item.statements.map((text, i) => <li key={i}>{text}</li>)}</ul></details>}
      {item.full_transcript && (
        <details open={defaultOpenTranscript} style={{ marginTop: '8px' }}>
          <summary><strong>Vollständiges Wort-für-Wort-Transkript des Gesprächs</strong></summary>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              fontSize: '0.92rem',
              lineHeight: 1.55,
              background: '#FAF8F3',
              border: '1px solid #E0D8C8',
              borderRadius: '6px',
              padding: '10px 12px',
              marginTop: '6px',
              maxHeight: '360px',
              overflowY: 'auto',
            }}
          >
            {item.full_transcript}
          </pre>
        </details>
      )}
    </article>
  );
}

// collapsed: Terminseite – das Wichtigste steht schon oben; die Auswertung lässt sich aufklappen (keine Wiederholung).
export function ConversationInsights({ leadId, collapsed = false }: { leadId: string; collapsed?: boolean }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const query = useQuery({
    queryKey: ['projects', project.id, 'conversation-insights', leadId],
    queryFn: ({ signal }) => papi.leadConversationInsights(leadId, signal),
    enabled: can('lead.read'),
    refetchInterval: 30000,
    retry: false,
  });
  if (!can('lead.read')) return null;
  // Eingeklappt (Terminseite) erst zeigen, wenn es eine Auswertung gibt – kein Lade- oder Leerzustand zwischen den Karten.
  if (collapsed && !(query.isSuccess && query.data.items.length > 0)) return null;
  const body = (
    <>
      <p className="muted">Automatisch aus den Aufzeichnungen – ohne Bestätigung sichtbar. Bestehende Stammdaten werden dadurch nicht überschrieben.</p>
      {query.isPending ? <p className="muted">Auswertungen werden geladen …</p>
        : query.isError ? <p role="status">Gesprächserkenntnisse konnten nicht geladen werden. <button type="button" className="btn btn-small" onClick={() => void query.refetch()}>Erneut laden</button></p>
          : !query.data.items.length ? <p className="muted">Noch keine verfügbare Gesprächsauswertung.</p>
            : <>
              {query.data.items[0] && <Insight item={query.data.items[0]} projectId={project.id} defaultOpenTranscript />}
              {query.data.items.length > 1 && <details><summary>Frühere Gesprächsauswertungen ({query.data.items.length - 1})</summary>{query.data.items.slice(1).map((item) => <Insight key={item.call_id} item={item} projectId={project.id} />)}</details>}
            </>}
      {can('call.start') && <p><Link to={`/p/${project.key}/akquise/${leadId}`}>Gespräche und Prüfhinweise im Arbeitsplatz öffnen</Link></p>}
    </>
  );
  if (collapsed)
    return (
      <section className="card insights-collapsed" id="gespraechserkenntnisse" aria-label="Gesprächserkenntnisse">
        <details open>
          <summary>
            <strong>Gesprächserkenntnisse &amp; Volltranskript</strong> <span className="muted">– KI-Auswertung, Audio &amp; Wort-für-Wort-Transkript</span>
          </summary>
          {body}
        </details>
      </section>
    );
  return (
    <section className="card" id="gespraechserkenntnisse" aria-label="Gesprächserkenntnisse">
      <h2>Gesprächserkenntnisse</h2>
      {body}
    </section>
  );
}
